import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Button } from '@rocco/components/button';
import { DatePicker } from '@rocco/components/date-picker';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@rocco/components/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from '@rocco/components/form';
import { RadioGroup, RadioGroupItem } from '@rocco/components/radio-group';
import type {
    ContactSuggestion,
    DirectConversation,
    Participant,
} from '../../../types';
import { useMigrateMessagesPreview } from '../../../hooks/use-migrate-messages-preview';
import { AddParticipantInput } from './AddParticipantInput';
import { MessagesMigrationPreview } from './MessagesMigrationPreview';

type MessageMigrationUIOption =
    | 'AllMessages'
    | 'TodayOnly'
    | 'FromDate'
    | 'None';

const formSchema = z
    .object({
        messageMigration: z.enum(
            ['AllMessages', 'TodayOnly', 'FromDate', 'None'],
            {
                invalid_type_error: 'Please select a message history option',
                required_error: 'Please select a message history option',
            },
        ),
        messageMigrationFromDate: z.date().optional(),
    })
    .refine(
        (data) => {
            if (data.messageMigration === 'FromDate') {
                return data.messageMigrationFromDate != null;
            }
            return true;
        },
        {
            message: 'Please select a date',
            path: ['messageMigrationFromDate'],
        },
    );

type FormValues = z.infer<typeof formSchema>;

/**
 * Dialog component for inviting participants to a 1-on-1 conversation by finding/creating
 * a group chat. Allows users to select participants to add and choose which messages
 * to migrate to the new group chat.
 * @see {@link ChatBoxSettingsMembers}
 */
export function InviteToGroupChatDialog({
    open,
    conversation,
    isSubmitting = false,
    onOpenChange,
    onConfirm,
}: {
    open: boolean;
    /** The source 1-on-1 conversation to invite participants to. */
    conversation: DirectConversation;
    isSubmitting?: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: (
        memberIDs: number[],
        migrateMessagesFrom: number | null,
    ) => void;
}) {
    const formRef = useRef<HTMLFormElement>(null);
    const [step, setStep] = useState<'participants' | 'migration'>(
        'participants',
    );
    const [participantsToAdd, setParticipantsToAdd] = useState<
        ContactSuggestion[]
    >([]);
    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        mode: 'onSubmit',
        defaultValues: {
            messageMigration: 'TodayOnly',
            messageMigrationFromDate: undefined,
        },
        disabled: isSubmitting,
    });

    function onSubmit(values: FormValues) {
        const memberIDs = participantsToAdd.map((p) => p.ID);
        const migrateMessagesFrom = getMigrateMessagesFromTimestamp(
            values.messageMigration,
            values.messageMigrationFromDate,
        );
        onConfirm(memberIDs, migrateMessagesFrom);
    }

    // Reset state when dialog is closed.
    useEffect(() => {
        if (!open) {
            setStep('participants');
            setParticipantsToAdd([]);
            form.reset();
        }
    }, [open, form]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="grid max-h-[95vh] max-w-[480px] grid-cols-1 overflow-y-auto"
                // Need modal overlay to be on top of the inbox drawer.
                overlayClassName="z-modal!"
                onEscapeKeyDown={(e) => {
                    if (isSubmitting) {
                        e.preventDefault();
                    }
                }}
                onPointerDownOutside={(e) => {
                    if (isSubmitting) {
                        e.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>
                        {step === 'participants'
                            ? 'Add people to this conversation'
                            : 'Include conversation history?'}
                    </DialogTitle>
                    <DialogDescription>
                        {step === 'participants'
                            ? ''
                            : "Choose whether the added users can see past messages and files once they've been added to the conversation."}
                    </DialogDescription>
                </DialogHeader>
                {step === 'participants' ? (
                    <ParticipantSelectionStep
                        participants={conversation.Participants}
                        participantsToAdd={participantsToAdd}
                        isDisabled={isSubmitting}
                        onParticipantsChange={setParticipantsToAdd}
                    />
                ) : (
                    <MigrationOptionsStep
                        conversation={conversation}
                        formRef={formRef}
                        form={form}
                        isSubmitting={isSubmitting}
                        onSubmit={onSubmit}
                    />
                )}
                <DialogFooter className="flex-row items-center">
                    {step === 'migration' && (
                        <p className="mr-auto text-[11px] leading-tight text-gray-600">
                            Once included, it can't be undone.
                        </p>
                    )}
                    <Button
                        type="button"
                        className="px-[16px] py-[6px]"
                        color="outline"
                        disabled={isSubmitting}
                        onClick={() => {
                            if (step === 'participants') {
                                onOpenChange(false);
                            } else {
                                setStep('participants');
                            }
                        }}
                    >
                        {step === 'participants' ? 'Cancel' : 'Back'}
                    </Button>
                    <Button
                        type="button"
                        className="min-w-[88px] px-[16px] py-[6px]"
                        color="dark"
                        isLoading={isSubmitting}
                        disabled={
                            participantsToAdd.length === 0 || isSubmitting
                        }
                        onClick={() => {
                            if (isSubmitting) return;
                            if (step === 'participants') {
                                if (participantsToAdd.length > 0) {
                                    setStep('migration');
                                }
                            } else {
                                formRef.current?.requestSubmit();
                            }
                        }}
                    >
                        {step === 'participants' ? 'Next' : 'Finished'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Step 1: Select participants to add to the conversation.
 */
function ParticipantSelectionStep({
    participants,
    participantsToAdd,
    isDisabled,
    onParticipantsChange,
}: {
    participants: Participant[];
    participantsToAdd: ContactSuggestion[];
    isDisabled: boolean;
    onParticipantsChange: (participants: ContactSuggestion[]) => void;
}) {
    return (
        <AddParticipantInput
            className="max-h-[360px] min-h-[120px]"
            modal
            participants={participants}
            participantsToAdd={participantsToAdd}
            isDisabled={isDisabled}
            onParticipantsChange={onParticipantsChange}
        />
    );
}

/**
 * Step 2: Select message migration options.
 */
function MigrationOptionsStep({
    conversation,
    formRef,
    form,
    isSubmitting,
    onSubmit,
}: {
    conversation: DirectConversation;
    formRef: React.RefObject<HTMLFormElement>;
    form: ReturnType<typeof useForm<FormValues>>;
    isSubmitting: boolean;
    onSubmit: (values: FormValues) => void;
}) {
    const id = useId();
    const formId = `invite-to-group-chat-form-${id}`;
    const [scrollUpCounter, setScrollUpCounter] = useState(0);
    const selectedMigration = form.watch('messageMigration');
    const selectedFromDate = form.watch('messageMigrationFromDate');
    const previewFromTimestamp = useMemo(() => {
        return getMigrateMessagesFromTimestamp(
            selectedMigration,
            selectedFromDate,
        );
    }, [selectedMigration, selectedFromDate]);
    const { messages, isLoading, isEmpty, hasMore, loadMore } =
        useMigrateMessagesPreview({
            conversationID: Number(conversation.ID),
            fromTimestamp: previewFromTimestamp,
        });

    // Scroll up preview messages to the top when the timestamp changes.
    useEffect(() => {
        setScrollUpCounter((state) => state + 1);
    }, [previewFromTimestamp]);

    return (
        <Form {...form}>
            <form
                ref={formRef}
                id={formId}
                className="space-y-[8px]"
                onSubmit={form.handleSubmit(onSubmit)}
            >
                <FormField
                    name="messageMigration"
                    control={form.control}
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <RadioGroup
                                    className="gap-0"
                                    name={field.name}
                                    value={field.value}
                                    disabled={field.disabled}
                                    onValueChange={field.onChange}
                                >
                                    <MigrationOption
                                        value="None"
                                        label="Don't include any conversation history"
                                    />
                                    <MigrationOption
                                        value="TodayOnly"
                                        label="From today"
                                    />
                                    <MigrationOption
                                        value="AllMessages"
                                        label="Include all message history"
                                    />
                                    <MigrationOption
                                        value="FromDate"
                                        label="Custom"
                                    />
                                </RadioGroup>
                            </FormControl>
                            <FormMessage
                                className="text-3.5! leading-tight!"
                                variant="darker"
                            />
                        </FormItem>
                    )}
                />
                {selectedMigration === 'FromDate' && (
                    <FormField
                        name="messageMigrationFromDate"
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className="ml-[32px]">
                                <FormControl>
                                    <DatePicker
                                        className="w-fit min-w-[240px]"
                                        value={field.value}
                                        disabled={
                                            field.disabled || isSubmitting
                                        }
                                        minDate={new Date('2000-01-01')}
                                        maxDate={new Date()}
                                        placeholder="Select date"
                                        onChange={field.onChange}
                                    />
                                </FormControl>
                                <FormMessage
                                    className="text-3.5! leading-tight!"
                                    variant="darker"
                                />
                            </FormItem>
                        )}
                    />
                )}
            </form>
            {/* Show preview when user chooses to migrate some messages */}
            {selectedMigration !== 'None' && (
                <MessagesMigrationPreview
                    className="mt-[8px]"
                    messages={messages}
                    placeholderText={
                        selectedMigration === 'FromDate' && !selectedFromDate
                            ? 'Select a date to preview messages.'
                            : undefined
                    }
                    scrollUpCounter={scrollUpCounter}
                    isLoading={isLoading}
                    isEmpty={isEmpty}
                    hasMore={hasMore}
                    onLoadMore={loadMore}
                />
            )}
        </Form>
    );
}

/**
 * A radio option for the message migration selection.
 */
function MigrationOption({
    value,
    label,
}: {
    value: MessageMigrationUIOption;
    label: string;
}) {
    return (
        <label className="flex items-center gap-[8px] p-[8px] has-data-[state=checked]:bg-gray-50">
            <RadioGroupItem
                className="aspect-square size-[16px] shrink-0"
                value={value}
            />
            <span className="min-w-0 text-[12px] font-medium leading-tight">
                {label}
            </span>
        </label>
    );
}

/**
 * Convert the UI migration option to a unix timestamp for the API.
 * - AllMessages: 0 (include all messages)
 * - TodayOnly: timestamp of start of today in browser timezone
 * - FromDate: timestamp of start of selected date in browser timezone
 * - None: null (no messages)
 */
function getMigrateMessagesFromTimestamp(
    option: MessageMigrationUIOption,
    fromDate?: Date,
): number | null {
    switch (option) {
        case 'AllMessages':
            return 0;
        case 'TodayOnly': {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return Math.floor(today.getTime() / 1000);
        }
        case 'FromDate': {
            if (!fromDate) return null;
            const date = new Date(fromDate);
            date.setHours(0, 0, 0, 0);
            return Math.floor(date.getTime() / 1000);
        }
        case 'None':
        default:
            return null;
    }
}
